import { NgRedux } from '@angular-redux/store';
import { Inject, Injectable } from '@angular/core';
import { ReservationCartItem } from '../../common/model/shopping-cart';
import { Actions } from '../../store/actions';
import { IAppConfig } from '../../store/interfaces/IAppConfig';
import { AppAction, AppActionState, IAppState } from '../../store/interfaces/IAppState';
import { ReservationChangeUtils } from '../../store/util/reservation-changes.util';
import { APP_CONFIG } from '../config';
import { Guest } from '../model/guest/guest.model';
import { ReservationChangeType } from '../model/reservation/change/reservation-change.model';
import { Reservation } from '../model/reservation/reservation.model';
import { ReservationStayPreferences } from '../model/reservation/stay-preferences/reservation-stay-preferences.model';
import { User } from '../model/user/user.model';
import { HotelService } from '../services/hotel.service';
import { LogService } from '../services/logging.service';
import { MemberService } from '../services/member.service';
import { PointsEstimateService } from '../services/points-estimate.service';
import { ReservationAvailabilityService } from '../services/reservation-availability.service';
import { ReservationStayPreferencesService } from '../services/reservation-stay-preferences.service';
import { ReservationService } from '../services/reservation.service';
import { ShoppingCartService } from '../services/shopping-cart.service';
import { CommonActionsDelegate } from './common-actions.delegate';

const CLASS_NAME = 'ModifyReservationDelegate';

@Injectable()
export class ModifyReservationDelegate extends CommonActionsDelegate {
  private _appConfig: IAppConfig;

  constructor(
    @Inject(APP_CONFIG) appConfig,
    _ngRedux: NgRedux<IAppState>,
    _actions: Actions,
    _logService: LogService,
    _reservationService: ReservationService,
    private _shoppingCartService: ShoppingCartService,
    private _reservationAvailabilityService: ReservationAvailabilityService,
    _hotelService: HotelService,
    private _stayPreferencesService: ReservationStayPreferencesService,
    _memberService: MemberService,
    private _pointsEstimateService: PointsEstimateService
  ) {
    super(_ngRedux, _actions, _logService, _reservationService, _hotelService, _memberService);
    this._appConfig = appConfig;
  }

  addChange(changeType: ReservationChangeType, value: any): void {
    const reservationChanges: Map<ReservationChangeType, any> = ReservationChangeUtils.mergeChange(this._ngRedux.getState().reservation, this._ngRedux.getState().reservationChanges, changeType, value);
    this._actions.dispatchReservationChangesAction(AppAction.UPDATE_CHANGES, AppActionState.SUCCESS, reservationChanges);
  }

  clearReservationChanges(changesToClear: Array<ReservationChangeType>) {
    const reservationChanges: Map<ReservationChangeType, any> = new Map<ReservationChangeType, any>(this._ngRedux.getState().reservationChanges.entries());
    changesToClear.forEach(changeType => {
      reservationChanges.delete(changeType);
    });
    this._actions.dispatchReservationChangesAction(AppAction.CLEAR_CHANGES, AppActionState.SUCCESS, reservationChanges);
  }

  clearAllChanges() {
    this._shoppingCartService.clearCart();
    this._actions.dispatchReservationChangesAction(AppAction.CLEAR_ALL_CHANGES, AppActionState.SUCCESS, new Map<ReservationChangeType, any>());
  }

  checkReservationAvailability(): void {
    this._actions.dispatchAvailableReservationAction(AppAction.CHECK_AVAILABILITY, AppActionState.INITIAL);
    const reservationChanges: Map<ReservationChangeType, any> = this._ngRedux.getState().reservationChanges;
    const reservation: Reservation = this._ngRedux.getState().reservation;
    if (reservationChanges.size === 0) {
      // There are not any changes, we do not need to check availability
      this._actions.dispatchAvailableReservationAction(AppAction.CHECK_AVAILABILITY, AppActionState.SUCCESS);
      this._shoppingCartService.clearCart();
      return;
    }
    this._reservationAvailabilityService.getReservationAvailability(reservation, reservationChanges, this._ngRedux.getState().member, true)
      .subscribe(availableReservation => {
          this._logService.info(`Availability successfully checked for Reservation: ${reservation.confirmationNumber}`, CLASS_NAME);
          this._actions.dispatchAvailableReservationAction(AppAction.CHECK_AVAILABILITY, AppActionState.SUCCESS, availableReservation);
          this._shoppingCartService.addReservationInCart(availableReservation);
        },
        error => {
          this._logService.warn(`Error checking availability for: ${reservation.confirmationNumber}`, CLASS_NAME, error);
          this._actions.dispatchAvailableReservationAction(AppAction.CHECK_AVAILABILITY, AppActionState.ERROR, null, error);
        }
      );
  }

  updateReservation() {
    this._actions.dispatchReservationAction(AppAction.MODIFY_RESERVATION, AppActionState.INITIAL, this._ngRedux.getState().reservation);
    const reservation: Reservation = this._ngRedux.getState().reservation;
    const reservationChanges: Map<ReservationChangeType, any> = this._ngRedux.getState().reservationChanges;
    const availableReservation: Reservation = this._ngRedux.getState().availableReservation;
    const rateDetails = this._ngRedux.getState().rateDetails;
    this._reservationService.updateReservation(reservation, reservationChanges, availableReservation, rateDetails)
      .subscribe(updatedReservation => {
          this._logService.info(`Reservation successfully modified: ${reservation.confirmationNumber}`, CLASS_NAME);
          this._shoppingCartService.clearCart();
          this._actions.dispatchReservationChangesAction(AppAction.UPDATE_CHANGES, AppActionState.SUCCESS, new Map<ReservationChangeType, any>());
          this._actions.dispatchReservationAction(AppAction.MODIFY_RESERVATION, AppActionState.SUCCESS, updatedReservation);
        },
        error => {
          this._logService.warn(`Error modifying reservation: ${reservation.confirmationNumber}`, CLASS_NAME, error);
          this._actions.dispatchReservationAction(AppAction.MODIFY_RESERVATION, AppActionState.ERROR, this._ngRedux.getState().reservation, error);
        }
      );
  }

  updateStayPrefernces(reservation: Reservation) {
    this._actions.dispatchStayPreferencesAction(AppAction.MODIFY_STAY_PREFERENCES, AppActionState.INITIAL, this._ngRedux.getState().stayPreferences);
    const stayPreferences: ReservationStayPreferences = this._ngRedux.getState().stayPreferences;
    this._stayPreferencesService.setReservationStayPreferences(reservation, stayPreferences)
      .subscribe(response => {
          this._actions.dispatchStayPreferencesAction(AppAction.MODIFY_STAY_PREFERENCES, AppActionState.SUCCESS, stayPreferences);
        },
        error => {
          if (!!error.code && (error.code === 200)) {
            this._actions.dispatchStayPreferencesAction(AppAction.MODIFY_STAY_PREFERENCES, AppActionState.SUCCESS, stayPreferences);
          } else {
            this._logService.warn(`Error retrieving Reservation stay preferences: ${reservation.confirmationNumber}`, CLASS_NAME, error);
            this._actions.dispatchStayPreferencesAction(AppAction.MODIFY_STAY_PREFERENCES, AppActionState.ERROR, this._ngRedux.getState().stayPreferences, error);
          }
        }
      );
  }

  editReservationStayPreferences(stayPreferences: ReservationStayPreferences) {
    // Also edit the state of the account stay preference
    this._actions.dispatchStayPreferencesAction(AppAction.MODIFY_STAY_PREFERENCES, AppActionState.INITIAL, stayPreferences);
  }

  addAdditionalGuestNames(additionalGuests: Array<Guest>) {
    this._actions.dispatchReservationAction(AppAction.ADD_ADDITIONAL_GUEST_NAMES, AppActionState.INITIAL, this._ngRedux.getState().reservation);
    const reservation: Reservation = this._ngRedux.getState().reservation;
    const rateDetails = this._ngRedux.getState().rateDetails;
    this._reservationService.updateReservation(reservation, new Map<ReservationChangeType, any>([[ReservationChangeType.ADDITIONAL_GUEST_NAMES, additionalGuests]]), reservation, rateDetails)
      .subscribe(updatedReservation => {
          this._actions.dispatchReservationAction(AppAction.ADD_ADDITIONAL_GUEST_NAMES, AppActionState.SUCCESS, Object.assign({}, this._ngRedux.getState().reservation, {additionalGuests: updatedReservation.additionalGuests}));
        },
        error => {
          this._logService.warn(`Error adding additional guest names: ${reservation.confirmationNumber}`, CLASS_NAME, error);
          this._actions.dispatchReservationAction(AppAction.ADD_ADDITIONAL_GUEST_NAMES, AppActionState.ERROR, this._ngRedux.getState().reservation, error);
        }
      );
  }

  fetchSessionReservationChanges(reservation: Reservation) {
    this._shoppingCartService.getReservationItemFromCart(reservation)
      .subscribe((existingReservationChanges: ReservationCartItem) => {
          let reservationChanges: Map<ReservationChangeType, any> = new Map<ReservationChangeType, any>(this._ngRedux.getState().reservationChanges.entries());
          reservationChanges = ReservationChangeUtils.mergeChange(reservation, reservationChanges, ReservationChangeType.NUMBER_OF_ROOMS, existingReservationChanges.numRooms);
          reservationChanges = ReservationChangeUtils.mergeChange(reservation, reservationChanges, ReservationChangeType.NUMBER_OF_CHILDREN, existingReservationChanges.numChildren);
          reservationChanges = ReservationChangeUtils.mergeChange(reservation, reservationChanges, ReservationChangeType.NUMBER_OF_ADULTS, existingReservationChanges.numAdults);
          reservationChanges = ReservationChangeUtils.mergeChange(reservation, reservationChanges, ReservationChangeType.CHECK_IN_DATE, existingReservationChanges.checkinDate);
          reservationChanges = ReservationChangeUtils.mergeChange(reservation, reservationChanges, ReservationChangeType.CHECK_OUT_DATE, existingReservationChanges.checkoutDate);
          reservationChanges = ReservationChangeUtils.mergeChange(reservation, reservationChanges, ReservationChangeType.RATE, existingReservationChanges.rateCode);
          reservationChanges = ReservationChangeUtils.mergeChange(reservation, reservationChanges, ReservationChangeType.ROOM, existingReservationChanges.roomCode);
          reservationChanges = ReservationChangeUtils.mergeChange(reservation, reservationChanges, ReservationChangeType.GROUP_CODE, existingReservationChanges.groupCode);
          this._actions.dispatchReservationChangesAction(AppAction.LOAD_CHANGES, AppActionState.SUCCESS, reservationChanges);
        },
        (error) => {
          this._actions.dispatchReservationChangesAction(AppAction.LOAD_CHANGES, AppActionState.ERROR, new Map<ReservationChangeType, any>());
          this._logService.warn(`Error looking for changes in session for Reservation #: ${reservation.confirmationNumber} `, CLASS_NAME, error);
        });
  }

  fetchStayPreferences(reservation: Reservation, mobileApp: boolean = false) {
    this._actions.dispatchStayPreferencesAction(AppAction.GET_STAY_PREFERENCES, AppActionState.INITIAL);
    this._stayPreferencesService.getReservationStayPreferences(reservation, mobileApp)
      .subscribe(stayPreferences => {
          this._actions.dispatchStayPreferencesAction(AppAction.GET_STAY_PREFERENCES, AppActionState.SUCCESS, stayPreferences);
        },
        error => {
          this._logService.warn(`Error retrieving Reservation stay preferences: ${reservation.confirmationNumber}`, CLASS_NAME, error);
          this._actions.dispatchStayPreferencesAction(AppAction.GET_STAY_PREFERENCES, AppActionState.ERROR, null, error);
        }
      );
  }

  fetchPointsEstimate(reservation: Reservation) {
    this._actions.dispatchPointsEstimateAction(AppAction.GET_POINTS_ESTIMATE, AppActionState.INITIAL, this._ngRedux.getState().earningsEstimate);
    this._pointsEstimateService.getReservationPointsEstimate(reservation)
      .subscribe(points => {
          this._actions.dispatchPointsEstimateAction(AppAction.GET_POINTS_ESTIMATE, AppActionState.SUCCESS, points);
        },
        error => {
          this._logService.warn(`Error retrieving Reservation points estimate: ${reservation.confirmationNumber}`, CLASS_NAME, error);
          this._actions.dispatchPointsEstimateAction(AppAction.GET_POINTS_ESTIMATE, AppActionState.ERROR, null, error);
        }
      );
  }

  associateReservation(reservation: Reservation, user: User, overrideGuest: boolean = false) {
    const stateReservation = this._ngRedux.getState().reservation;
    this._actions.dispatchReservationAction(AppAction.ASSOCIATE_RESERVATION, AppActionState.INITIAL, stateReservation);
    this._reservationService.associate(reservation, user, overrideGuest)
      .subscribe((response: Reservation) => {
          const associatedReservation = Object.assign(new Reservation(reservation.confirmationNumber), stateReservation, {guest: response.guest});
          this._actions.dispatchReservationAction(AppAction.ASSOCIATE_RESERVATION, AppActionState.SUCCESS, associatedReservation);
        },
        error => {
          this._actions.dispatchReservationAction(AppAction.ASSOCIATE_RESERVATION, AppActionState.ERROR, stateReservation, error);
        });
  }

  joinAndAssociateReservation(reservation: Reservation, simplifiedLoginEnabled = false) {
    const stateReservation = this._ngRedux.getState().reservation;
    this._actions.dispatchReservationAction(AppAction.JOIN_ASSOCIATE_RESERVATION, AppActionState.INITIAL, stateReservation);
    this._memberService.enroll(reservation, simplifiedLoginEnabled)
      .subscribe((response: User) => {
          const guest = Object.assign(new Guest(), stateReservation.guest, {rewardsClubNumber: response.rewardsClubNumber});
          const associatedReservation = Object.assign(new Reservation(reservation.confirmationNumber), stateReservation, {guest: guest});
          this._actions.dispatchMemberAction(AppAction.GET_MEMBER, AppActionState.SUCCESS, response);
          this._actions.dispatchReservationAction(AppAction.JOIN_ASSOCIATE_RESERVATION, AppActionState.SUCCESS, associatedReservation);
        },
        error => {
          this._actions.dispatchReservationAction(AppAction.JOIN_ASSOCIATE_RESERVATION, AppActionState.ERROR, stateReservation, error);
        });
  }
}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/api/delegates/modify-reservation.delegate.ts